import { addDays } from "date-fns/addDays";
import { addMonths } from "date-fns/addMonths";
import { format } from "date-fns/format";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatDateChipLabel, getDateRangeFromFormDates, normalizeToFormDateString, parseFormDateString } from "../utils/form-date.js";
const FORM_DATE_FORMAT = 'yyyyMMdd';
function resolveAiSearchFormDates(startDate, endDate, referenceDate = new Date()) {
    const normalizedStartDate = normalizeToFormDateString(startDate);
    const normalizedEndDate = normalizeToFormDateString(endDate);
    if (!normalizedStartDate && !normalizedEndDate) return {
        startDate: format(referenceDate, FORM_DATE_FORMAT),
        endDate: format(addDays(referenceDate, 1), FORM_DATE_FORMAT),
        usedDefaults: true
    };
    return {
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
        usedDefaults: false
    };
}
const STAY_SEARCH_CALENDAR_MONTHS = 13;
const MAX_STAY_NIGHTS = 28;
function useAiSearchDateChip({ startDate, endDate, onDatesChange }) {
    const currentDate = useMemo(()=>new Date(), []);
    const { startDate: effectiveStartDate, endDate: effectiveEndDate, usedDefaults: shouldUseDefaultDates } = useMemo(()=>resolveAiSearchFormDates(startDate, endDate, currentDate), [
        currentDate,
        endDate,
        startDate
    ]);
    const [maxSelectableDate, setMaxSelectableDate] = useState();
    const [defaultMonth, setDefaultMonth] = useState(()=>parseFormDateString(effectiveStartDate, currentDate) ?? currentDate);
    const didSeedDefaultDatesRef = useRef(false);
    const selectedDates = useMemo(()=>getDateRangeFromFormDates(effectiveStartDate, effectiveEndDate, currentDate), [
        currentDate,
        effectiveEndDate,
        effectiveStartDate
    ]);
    const dateChipLabel = useMemo(()=>formatDateChipLabel(effectiveStartDate, effectiveEndDate, currentDate), [
        currentDate,
        effectiveEndDate,
        effectiveStartDate
    ]);
    useLayoutEffect(()=>{
        if (!shouldUseDefaultDates) {
            didSeedDefaultDatesRef.current = false;
            return;
        }
        if (didSeedDefaultDatesRef.current) return;
        didSeedDefaultDatesRef.current = true;
        onDatesChange(effectiveStartDate, effectiveEndDate, 'defaultSeed');
    }, [
        effectiveEndDate,
        effectiveStartDate,
        onDatesChange,
        shouldUseDefaultDates
    ]);
    useEffect(()=>{
        const nextDefaultMonth = parseFormDateString(startDate, currentDate);
        if (nextDefaultMonth) setDefaultMonth(nextDefaultMonth);
    }, [
        currentDate,
        startDate
    ]);
    const latestDatesRef = useRef(selectedDates);
    useEffect(()=>{
        latestDatesRef.current = selectedDates;
    }, [
        selectedDates
    ]);
    const handleCalendarChange = useCallback((dates, triggerDate)=>{
        latestDatesRef.current = dates?.from ? dates : void 0;
        if (!dates?.from) {
            onDatesChange('', '', 'user');
            setDefaultMonth(triggerDate);
            setMaxSelectableDate(void 0);
            return;
        }
        onDatesChange(format(dates.from, FORM_DATE_FORMAT), dates.to ? format(dates.to, FORM_DATE_FORMAT) : '', 'user');
        setDefaultMonth(dates.from);
        setMaxSelectableDate(dates.to ? void 0 : addDays(dates.from, MAX_STAY_NIGHTS));
    }, [
        onDatesChange
    ]);
    const handleDateOpenChange = useCallback((open)=>{
        if (open) return;
        const dates = latestDatesRef.current;
        if (!dates?.from || dates.to) return;
        onDatesChange(format(dates.from, FORM_DATE_FORMAT), format(addDays(dates.from, 1), FORM_DATE_FORMAT), 'user');
        setMaxSelectableDate(void 0);
    }, [
        onDatesChange
    ]);
    const datePickerProps = useMemo(()=>({
            selected: selectedDates,
            handleCalendarChange,
            onOpenChange: handleDateOpenChange,
            defaultMonth,
            startMonth: currentDate,
            endMonth: addMonths(currentDate, STAY_SEARCH_CALENDAR_MONTHS - 1),
            disabled: {
                before: currentDate,
                after: maxSelectableDate
            }
        }), [
        currentDate,
        defaultMonth,
        handleCalendarChange,
        handleDateOpenChange,
        maxSelectableDate,
        selectedDates
    ]);
    return {
        dateChipLabel,
        datePickerProps
    };
}
export { STAY_SEARCH_CALENDAR_MONTHS, resolveAiSearchFormDates, useAiSearchDateChip };
